import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LogOut, Home, Plus, Edit, Trash2, Star, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchTestimonials();
    fetchSettings();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/testimonials', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings-public');
      if (response.ok) {
        const data = await response.json();
        setIsEnabled(data.testimonials_enabled !== false);
      }
    } catch (error) {
      console.error('Settings fetch error:', error);
    }
  };

  const toggleEnabled = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ testimonials_enabled: !isEnabled })
      });

      if (response.ok) {
        setIsEnabled(!isEnabled);
        toast.success(isEnabled ? 'Referanslar gizlendi' : 'Referanslar aktif edildi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingTestimonial({
      name: '',
      project: '',
      rating: 5,
      comment: '',
      display_order: testimonials.length
    });
    setIsDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('admin_token');
      const isNew = !editingTestimonial.id;
      const url = isNew 
        ? '/api/admin/testimonials'
        : `/api/admin/testimonials/${editingTestimonial.id}`;
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingTestimonial)
      });

      if (response.ok) {
        toast.success(isNew ? 'Referans eklendi' : 'Referans güncellendi');
        setIsDialogOpen(false);
        fetchTestimonials();
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu referansı silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Referans silindi');
        fetchTestimonials();
      }
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Yükleniyor...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">Müşteri Referansları Yönetimi</h1>
            <div className="flex gap-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Ana Sayfa
                </Button>
              </Link>
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  Admin Panel
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold mb-2">Referanslar Bölümü</h2>
              <p className="text-sm text-gray-600">
                Ana sayfada müşteri referanslarını {isEnabled ? 'göster' : 'gizle'}
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={toggleEnabled}
                variant={isEnabled ? 'destructive' : 'default'}
              >
                {isEnabled ? '❌ Gizle' : '✅ Aktif Et'}
              </Button>
              <Button onClick={handleAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Yeni Referans Ekle
              </Button>
            </div>
          </div>
        </div>

        {/* Testimonials Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İsim</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proje</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Puan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Yorum</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {testimonials.map((testimonial) => (
                <tr key={testimonial.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {testimonial.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {testimonial.project}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                    {testimonial.comment}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <Button
                      onClick={() => handleEdit(testimonial)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(testimonial.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {testimonials.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Henüz referans eklenmemiş. "Yeni Referans Ekle" butonuna tıklayın.
            </div>
          )}
        </div>
      </div>

      {/* Edit/Add Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTestimonial?.id ? 'Referansı Düzenle' : 'Yeni Referans Ekle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>İsim *</Label>
              <Input
                value={editingTestimonial?.name || ''}
                onChange={(e) => setEditingTestimonial({...editingTestimonial, name: e.target.value})}
                placeholder="Ahmet Yılmaz"
                required
              />
            </div>

            <div>
              <Label>Proje *</Label>
              <Input
                value={editingTestimonial?.project || ''}
                onChange={(e) => setEditingTestimonial({...editingTestimonial, project: e.target.value})}
                placeholder="Yenibosna Projesi"
                required
              />
            </div>

            <div>
              <Label>Puan (1-5) *</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setEditingTestimonial({...editingTestimonial, rating})}
                    className={`p-2 rounded ${
                      editingTestimonial?.rating >= rating 
                        ? 'text-yellow-400' 
                        : 'text-gray-300'
                    }`}
                  >
                    <Star className="h-8 w-8 fill-current" />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Yorum *</Label>
              <Textarea
                value={editingTestimonial?.comment || ''}
                onChange={(e) => setEditingTestimonial({...editingTestimonial, comment: e.target.value})}
                placeholder="Mükemmel bir deneyimdi..."
                rows={4}
                required
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestimonials;
